import { CSSProperties } from "react";
import { createTheme } from "@mui/material/styles";
import AppButton from "../../UI/AppButtons";
import { PaymentChoiceBlockProps } from "./PaymentChoiceOnCard.props";
import { formatPriceWithCurrencySymbol } from "../../../helpers/getPriceWithCurrencySymbol";
import { formatTheme } from "../../../config/MuiColorThemes";

export function PaymentChoiceBlock(props: PaymentChoiceBlockProps) {
  const {
    payNowDaily,
    payNowTotal,
    payAtPickupDaily,
    payAtPickupTotal,
    showSavingsBadge,
    savingsAmount,
    currencyCode,
    defaultCurrency,
    perDayLabel,
    totalLabel,
    selectLabel,
    savingsLabel,
    payAtPickupLabel,
    payNowLabel,
    onSelect,
    disabled,
    buttonBorderRadius,
    originalPayNowDaily,
    originalPayNowTotal,
    originalPayAtPickupDaily,
    originalPayAtPickupTotal,
    showOriginalPayNow,
    showOriginalPayAtPickup,
    compact,
    payNowDiscountLabel,
    payLaterSubLabel,
    savingsColor: savingsColorProp,
    originalColor: originalColorProp,
  } = props;

  const dynamicTheme = createTheme(formatTheme());
  const savingsColor =
    savingsColorProp ?? dynamicTheme?.palette["success-2"]?.main ?? "#33B627";
  const originalColor =
    originalColorProp ?? dynamicTheme?.palette["primary"]?.main ?? "#9e9e9e";

  const laterDaily = formatPriceWithCurrencySymbol(
    currencyCode,
    payAtPickupDaily
  );
  const laterTotal = formatPriceWithCurrencySymbol(
    currencyCode,
    payAtPickupTotal
  );
  const nowDaily = formatPriceWithCurrencySymbol(currencyCode, payNowDaily);
  const nowTotal = formatPriceWithCurrencySymbol(currencyCode, payNowTotal);

  // Guard: only format the crossed-out price when the flag is set AND a real value was provided.
  // Using `|| 0` would render a struck-through "€0.00" row when flag=true but the value is undefined.
  const origLaterDaily =
    showOriginalPayAtPickup && originalPayAtPickupDaily != null
      ? formatPriceWithCurrencySymbol(currencyCode, originalPayAtPickupDaily)
      : null;
  const origLaterTotal =
    showOriginalPayAtPickup && originalPayAtPickupTotal != null
      ? formatPriceWithCurrencySymbol(currencyCode, originalPayAtPickupTotal)
      : null;
  const origNowDaily =
    showOriginalPayNow && originalPayNowDaily != null
      ? formatPriceWithCurrencySymbol(currencyCode, originalPayNowDaily)
      : null;
  const origNowTotal =
    showOriginalPayNow && originalPayNowTotal != null
      ? formatPriceWithCurrencySymbol(currencyCode, originalPayNowTotal)
      : null;

  const px = compact ? "px-2" : "px-3";
  const headerSize = compact ? "text-[11px]" : "text-[13px]";
  const subLabelSize = compact ? "text-[9px]" : "text-[11px]";
  const origPriceSize = compact ? "0.78rem" : "0.9rem";
  const origPerDaySize = compact ? "0.72rem" : "0.85rem";
  const dailyFontSize = compact ? "0.95em" : "1.1em";
  const perDayFontSize = compact ? "0.85rem" : "1rem";
  const mt = compact ? "mt-0.5" : "mt-1";
  const ptBtn = compact ? "pt-1" : "pt-2";
  const btnClass = compact
    ? "min-w-[6rem] h-[2rem] rounded-md"
    : "min-w-[8rem] h-[2.75rem] rounded-md";

  // Shared cell styles
  const leftCell: CSSProperties = {
    borderRight: "1px solid #e5e7eb",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };
  const rightCell: CSSProperties = {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const hasOriginalRow = origLaterDaily || origNowDaily;
  // Kept in sync across every rendered card by ShowVehicles (tallest
  // discount/surcharge content wins) so card bottoms stay aligned.
  const subLabelRowMinHeight = "var(--payment-choice-sublabel-min-height, 0px)";

  const renderChoiceButton = (
    mode: "payAtPickup" | "payNow",
    isPrimary: boolean
  ) => (
    <AppButton
      disabled={disabled}
      color={isPrimary ? "primary" : undefined}
      variant={isPrimary ? undefined : "outlined"}
      className={btnClass}
      clickHandler={() => onSelect(mode)}
      style={{ borderRadius: `${buttonBorderRadius || 0}px` }}
    >
      <span
        className="text-x capitalize font-normal"
        style={{
          textTransform: "none",
          fontSize: compact ? "0.78rem" : undefined,
        }}
      >
        {selectLabel}
      </span>
    </AppButton>
  );

  return (
    <div
      className={`w-full border-t border-gray-200 grid grid-cols-2 ${
        compact ? "mt-1 pt-1" : "mt-2 pt-2"
      }`}
    >
      {/* ── Row: Column headers ── */}
      <div style={leftCell} className={`${px} pt-1 pb-0`}>
        <p
          className={`${headerSize} uppercase font-bold`}
          style={{ color: "rgba(0, 0, 0, 0.6)" }}
        >
          {payAtPickupLabel}
        </p>
      </div>
      <div style={rightCell} className={`${px} pt-1 pb-0`}>
        <p
          className={`${headerSize} uppercase font-bold`}
          style={{ color: "rgba(0, 0, 0, 0.6)" }}
        >
          {payNowLabel}
        </p>
      </div>

      {/* ── Row: Sub-labels (discount/surcharge text). Always rendered, even
          when empty, so every card reserves the same slot; the slot's
          min-height is kept in sync across all rendered cards (tallest
          content wins) via --payment-choice-sublabel-min-height, set by
          ShowVehicles so card bottoms/Scalapay widgets stay aligned. ── */}
      <div
        style={{ ...leftCell, minHeight: subLabelRowMinHeight }}
        className={`${px} pt-0.5 payment-choice-sublabel-cell`}
      >
        <div className="payment-choice-sublabel-content">
          {payLaterSubLabel && (
            <p
              className={`${subLabelSize} font-medium`}
              style={{ color: savingsColor, whiteSpace: "pre-line" }}
            >
              {payLaterSubLabel}
            </p>
          )}
        </div>
      </div>
      <div
        style={{ ...rightCell, minHeight: subLabelRowMinHeight }}
        className={`${px} pt-0.5 payment-choice-sublabel-cell`}
      >
        <div className="payment-choice-sublabel-content">
          {payNowDiscountLabel && (
            <p
              className={`${subLabelSize} font-medium`}
              style={{ color: savingsColor }}
            >
              {payNowDiscountLabel}
            </p>
          )}
        </div>
      </div>

      {/* ── Row: Original (crossed-out) daily price — only when at least one side has it ── */}
      {hasOriginalRow && (
        <>
          <div style={leftCell} className={`${px} ${mt}`}>
            {origLaterDaily ? (
              <p
                className="whitespace-nowrap"
                style={{
                  fontSize: origPriceSize,
                  textDecoration: "line-through",
                }}
              >
                <span style={{ color: originalColor }}>
                  {(defaultCurrency || origLaterDaily.symbol) +
                    " " +
                    origLaterDaily.price}
                </span>
                <span
                  className="font-normal"
                  style={{ fontSize: origPerDaySize, textTransform: "none" }}
                >
                  {" "}
                  {perDayLabel}
                </span>
              </p>
            ) : null}
          </div>
          <div style={rightCell} className={`${px} ${mt}`}>
            {origNowDaily ? (
              <p
                className="whitespace-nowrap"
                style={{
                  fontSize: origPriceSize,
                  textDecoration: "line-through",
                }}
              >
                <span style={{ color: originalColor }}>
                  {(defaultCurrency || origNowDaily.symbol) +
                    " " +
                    origNowDaily.price}
                </span>
                <span
                  className="font-normal"
                  style={{ fontSize: origPerDaySize, textTransform: "none" }}
                >
                  {" "}
                  {perDayLabel}
                </span>
              </p>
            ) : null}
          </div>
        </>
      )}

      {/* ── Row: Daily price ── */}
      <div style={leftCell} className={`${px} ${mt}`}>
        <p className="whitespace-nowrap text-black">
          <span style={{ fontSize: dailyFontSize, fontWeight: 700 }}>
            {(defaultCurrency || laterDaily.symbol) + " " + laterDaily.price}
          </span>
          <span
            className="font-normal"
            style={{ fontSize: perDayFontSize, textTransform: "none" }}
          >
            {" "}
            {perDayLabel}
          </span>
        </p>
      </div>
      <div style={rightCell} className={`${px} ${mt}`}>
        <p className="whitespace-nowrap text-black">
          <span style={{ fontSize: dailyFontSize, fontWeight: 700 }}>
            {(defaultCurrency || nowDaily.symbol) + " " + nowDaily.price}
          </span>
          <span
            className="font-normal"
            style={{ fontSize: perDayFontSize, textTransform: "none" }}
          >
            {" "}
            {perDayLabel}
          </span>
        </p>
      </div>

      {/* ── Row: Total price ── */}
      <div style={leftCell} className={`${px} ${mt}`}>
        <p
          className="text-[#707070] whitespace-nowrap font-normal"
          style={{ fontSize: "x-small" }}
        >
          {origLaterTotal && (
            <span
              className="mr-1"
              style={{ color: originalColor, textDecoration: "line-through" }}
            >
              {(defaultCurrency || origLaterTotal.symbol) +
                " " +
                origLaterTotal.price}
            </span>
          )}
          {laterTotal.symbol + " " + laterTotal.price} {totalLabel}
        </p>
      </div>
      <div style={rightCell} className={`${px} ${mt}`}>
        <p
          className="text-[#707070] whitespace-nowrap font-normal"
          style={{ fontSize: "x-small" }}
        >
          {origNowTotal && (
            <span
              className="mr-1"
              style={{ color: originalColor, textDecoration: "line-through" }}
            >
              {(defaultCurrency || origNowTotal.symbol) +
                " " +
                origNowTotal.price}
            </span>
          )}
          {nowTotal.symbol + " " + nowTotal.price} {totalLabel}
        </p>
      </div>

      {/* ── Row: Buttons — same grid row guarantees identical vertical position ── */}
      <div style={leftCell} className={`${px} ${ptBtn} pb-2`}>
        {renderChoiceButton("payAtPickup", false)}
      </div>
      <div style={rightCell} className={`${px} ${ptBtn} pb-2`}>
        {renderChoiceButton("payNow", !!showSavingsBadge)}
      </div>

      {/* ── Row: Savings badge (Pay Now only) ── */}
      {showSavingsBadge && savingsAmount > 0 && (
        <>
          <div style={leftCell} className={`${px} pb-1`} />
          <div style={rightCell} className={`${px} pb-1`}>
            <p
              className={`${compact ? "text-[10px]" : "text-xs"} font-bold`}
              style={{ color: savingsColor }}
            >
              {savingsLabel}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
